import { createRoute, redirect, type AnyRoute } from '@tanstack/react-router';
import { ConnectMcp } from '../../ui/pages/ConnectMcp';
import { ForgotPasswordPage } from '../../ui/pages/ForgotPasswordPage';
import { InviteAccept } from '../../ui/pages/InviteAccept';
import { LoginPage } from '../../ui/pages/LoginPage';
import { MfaChallengePage } from '../../ui/pages/MfaChallengePage';
import { ResetPasswordPage } from '../../ui/pages/ResetPasswordPage';
import { SignUpPage } from '../../ui/pages/SignUpPage';
import { SlackLinkPage } from '../../ui/pages/SlackLinkPage';
import { VerifyEmailPage } from '../../ui/pages/VerifyEmailPage';
import { sanitizeReturnPath } from '../../lib/login-return-to';

export const validateConnectMcpSearch = (search: Record<string, unknown>): { state?: string } => ({
  state: typeof search.state === 'string' ? search.state : undefined,
});

/** `returnTo` is attacker-controlled; only a sanitized in-app path survives. */
const validateReturnTo = (search: Record<string, unknown>): string | undefined =>
  typeof search.returnTo === 'string' ? (sanitizeReturnPath(search.returnTo) ?? undefined) : undefined;

export const validateInviteSearch = (
  search: Record<string, unknown>
): { invite?: string; error?: string } => ({
  invite:
    typeof search.invite === 'string' && search.invite.trim().length > 0
      ? search.invite
      : undefined,
  error: typeof search.error === 'string' ? search.error : undefined,
});

export type SignUpSearchParams = { invite?: string; error?: string; returnTo?: string };

export const validateSignUpSearch = (search: Record<string, unknown>): SignUpSearchParams => ({
  ...validateInviteSearch(search),
  returnTo: validateReturnTo(search),
});

export type VerifyEmailSearchParams = {
  email?: string;
  token?: string;
  invite?: string;
  sendFailed?: boolean;
  returnTo?: string;
};

export const validateVerifyEmailSearch = (
  search: Record<string, unknown>
): VerifyEmailSearchParams => ({
  email:
    typeof search.email === 'string' && search.email.trim().length > 0 ? search.email : undefined,
  token:
    typeof search.token === 'string' && search.token.trim().length > 0 ? search.token : undefined,
  invite:
    typeof search.invite === 'string' && search.invite.trim().length > 0
      ? search.invite
      : undefined,
  sendFailed: search.sendFailed === true || search.sendFailed === 'true' ? true : undefined,
  returnTo: validateReturnTo(search),
});

export type ResetPasswordSearchParams = {
  token?: string;
};

export const validateResetPasswordSearch = (
  search: Record<string, unknown>
): ResetPasswordSearchParams => ({
  token:
    typeof search.token === 'string' && search.token.trim().length > 0 ? search.token : undefined,
});

export const validateSlackLinkSearch = (search: Record<string, unknown>): { token?: string } => ({
  token: typeof search.token === 'string' ? search.token : undefined,
});

export type MfaChallengeMode = 'enroll' | 'verify';

export type MfaChallengeSearchParams = {
  mode: MfaChallengeMode;
  /** Validated in-app path to land on after verifying; absent on old links and the email sign-in without a deep link. */
  returnTo?: string;
};

export const validateMfaChallengeSearch = (
  search: Record<string, unknown>
): MfaChallengeSearchParams => ({
  mode: search.mode === 'enroll' ? 'enroll' : 'verify',
  returnTo: validateReturnTo(search),
});

export function createAuthRoutes<TRootRoute extends AnyRoute>(rootRoute: TRootRoute) {
  const inviteAcceptRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/invite/$token',
    component: InviteAccept,
  });

  const connectMcpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/connect/mcp',
    component: ConnectMcp,
    validateSearch: validateConnectMcpSearch,
  });

  // Legacy /connect (standalone Claude install page) -> Settings -> Integrations -> Coding agents (MCP).
  const connectRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/connect',
    beforeLoad: () => {
      throw redirect({ to: '/settings/$section', params: { section: 'claude' }, replace: true });
    },
  });

  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginPage,
    validateSearch: validateInviteSearch,
  });

  const mfaChallengeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login/mfa',
    component: MfaChallengePage,
    validateSearch: validateMfaChallengeSearch,
  });

  const slackLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/integrations/slack/link',
    component: SlackLinkPage,
    validateSearch: validateSlackLinkSearch,
  });

  const signupRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/signup',
    component: SignUpPage,
    validateSearch: validateSignUpSearch,
  });

  const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify-email',
    component: VerifyEmailPage,
    validateSearch: validateVerifyEmailSearch,
  });

  const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/forgot-password',
    component: ForgotPasswordPage,
  });

  const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reset-password',
    component: ResetPasswordPage,
    validateSearch: validateResetPasswordSearch,
  });

  return {
    connectMcpRoute,
    connectRoute,
    forgotPasswordRoute,
    inviteAcceptRoute,
    loginRoute,
    mfaChallengeRoute,
    resetPasswordRoute,
    signupRoute,
    slackLinkRoute,
    verifyEmailRoute,
  };
}
